import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, resolveUrl } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, LogOut, Upload, X, Trash2, Pin, Send, FileText, Home } from "lucide-react";

const isPdf = (a) => (a.type || "").toLowerCase() === "pdf" || (a.url || "").toLowerCase().includes(".pdf");
const EMPTY = { title: "", body: "", attachments: [], pinned: false };

function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError("");
    const res = await login(email, password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };
  return (
    <div className="min-h-screen grid place-items-center bg-[#0B0C0E] px-5">
      <form onSubmit={submit} data-testid="tavla-login" className="w-full max-w-sm rounded-2xl border border-white/10 bg-[#14161A] p-8">
        <div className="font-display text-2xl font-black uppercase text-white text-center">Anslagstavla</div>
        <div className="mt-1 text-center font-mono text-xs uppercase tracking-widest text-white/40">Logga in för att posta</div>
        <div className="mt-8 space-y-4">
          <div><Label className="text-white/70 text-xs uppercase">E-post</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1.5 bg-black/40 border-white/15" data-testid="tavla-login-email" /></div>
          <div><Label className="text-white/70 text-xs uppercase">Lösenord</Label><Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1.5 bg-black/40 border-white/15" data-testid="tavla-login-password" /></div>
          {error && <p className="text-red-400 text-sm" data-testid="tavla-login-error">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="tavla-login-submit" className="w-full bg-amber-500 text-black hover:bg-amber-400">{loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Logga in"}</Button>
        </div>
        <Link to="/anslagstavla" className="mt-6 block text-center text-xs text-white/40 hover:text-amber-500">← Till anslagstavlan</Link>
      </form>
    </div>
  );
}

export default function TavlaAdmin() {
  const { user, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = () => api.boardPosts().then(setPosts).catch(() => setPosts([]));
  useEffect(() => { if (user) load(); }, [user]);

  if (user === null) return <div className="min-h-screen grid place-items-center bg-[#0B0C0E]"><Loader2 className="w-8 h-8 animate-spin text-amber-500" /></div>;
  if (!user) return <Login />;
  if (!["admin", "board"].includes(user.role)) return <div className="min-h-screen grid place-items-center bg-[#0B0C0E] text-white/60">Ditt konto saknar behörighet att posta.</div>;

  const addFiles = async (files) => {
    setUploading(true);
    const added = [];
    for (const f of Array.from(files)) {
      try {
        const res = await api.upload(f);
        const url = res.url || res.file_url || res;
        added.push({ url, name: f.name, type: f.name.toLowerCase().endsWith(".pdf") ? "pdf" : "image" });
      } catch { toast.error(`Kunde inte ladda upp ${f.name}`); }
    }
    setForm((prev) => ({ ...prev, attachments: [...prev.attachments, ...added] }));
    setUploading(false);
    if (fileRef.current) fileRef.current.value = "";
  };

  const removeAtt = (i) => setForm((p) => ({ ...p, attachments: p.attachments.filter((_, x) => x !== i) }));

  const submit = async () => {
    if (!form.title.trim() && !form.body.trim() && form.attachments.length === 0) return toast.error("Skriv något eller bifoga en fil");
    setSaving(true);
    try {
      if (editId) { await api.updateBoardPost(editId, form); toast.success("Anslag uppdaterat"); }
      else { await api.createBoardPost(form); toast.success("Anslag publicerat"); }
      setForm(EMPTY); setEditId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Kunde inte spara"); }
    finally { setSaving(false); }
  };

  const startEdit = (p) => { setEditId(p.id); setForm({ title: p.title || "", body: p.body || "", attachments: p.attachments || [], pinned: !!p.pinned }); window.scrollTo({ top: 0, behavior: "smooth" }); };
  const del = async (id) => { if (!window.confirm("Ta bort anslaget?")) return; await api.deleteBoardPost(id); toast.success("Borttaget"); load(); };
  const togglePin = async (p) => { await api.updateBoardPost(p.id, { ...p, pinned: !p.pinned }); load(); };

  return (
    <div className="min-h-screen bg-[#0B0C0E]">
      <header className="flex items-center justify-between border-b border-white/10 px-5 sm:px-8 py-4">
        <div>
          <div className="font-display text-xl font-black uppercase text-white">Anslagstavla · Admin</div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-white/40">{user.name} · {user.email}</div>
        </div>
        <div className="flex items-center gap-3">
          <Link to="/anslagstavla" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-amber-500"><Home className="w-4 h-4" /> Tavlan</Link>
          <button onClick={logout} data-testid="tavla-logout" className="inline-flex items-center gap-1.5 text-sm text-white/60 hover:text-red-400"><LogOut className="w-4 h-4" /> Logga ut</button>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 sm:px-8 py-8 space-y-8">
        <div className="rounded-2xl border border-white/10 bg-[#14161A] p-6 space-y-4">
          <h2 className="font-display text-2xl font-black uppercase text-white">{editId ? "Redigera anslag" : "Nytt anslag"}</h2>
          <div><Label className="text-white/70 text-xs uppercase">Rubrik</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1.5 bg-black/40 border-white/15" data-testid="tavla-title" placeholder="T.ex. Startlista SS2 klar" /></div>
          <div><Label className="text-white/70 text-xs uppercase">Text</Label><Textarea rows={4} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} className="mt-1.5 bg-black/40 border-white/15" data-testid="tavla-body" placeholder="Skriv informationen här…" /></div>

          <div>
            <Label className="text-white/70 text-xs uppercase">Bilagor (PDF & bilder)</Label>
            <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" onChange={(e) => addFiles(e.target.files)} className="hidden" data-testid="tavla-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-1.5 inline-flex items-center gap-2 rounded-lg border border-dashed border-white/25 px-4 py-2.5 text-sm text-white/70 hover:border-amber-500 hover:text-amber-500 transition-colors" data-testid="tavla-upload-btn">
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />} Ladda upp filer
            </button>
            {form.attachments.length > 0 && (
              <div className="mt-3 space-y-2">
                {form.attachments.map((a, i) => (
                  <div key={i} className="flex items-center gap-3 rounded-lg border border-white/10 bg-black/30 p-2">
                    {isPdf(a) ? <FileText className="w-5 h-5 text-amber-400 shrink-0" /> : <img src={resolveUrl(a.url)} alt="" className="w-10 h-10 rounded object-cover shrink-0" />}
                    <span className="flex-1 text-sm text-white/70 truncate">{a.name}</span>
                    <button onClick={() => removeAtt(i)} className="p-1 text-white/40 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <label className="flex items-center gap-2 text-sm text-white/70 cursor-pointer">
            <input type="checkbox" checked={form.pinned} onChange={(e) => setForm({ ...form, pinned: e.target.checked })} className="accent-amber-500" data-testid="tavla-pin" />
            <Pin className="w-4 h-4 text-white/40" /> Fäst överst
          </label>

          <div className="flex gap-3">
            <Button onClick={submit} disabled={saving} className="bg-amber-500 text-black hover:bg-amber-400 font-bold uppercase tracking-widest" data-testid="tavla-submit">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-1" /> {editId ? "Spara" : "Publicera"}</>}
            </Button>
            {editId && <Button variant="ghost" onClick={() => { setForm(EMPTY); setEditId(null); }} className="text-white/60">Avbryt</Button>}
          </div>
        </div>

        <div>
          <h3 className="font-mono text-xs uppercase tracking-widest text-amber-500 mb-4">Publicerade anslag ({posts.length})</h3>
          <div className="space-y-2">
            {posts.map((p) => (
              <div key={p.id} data-testid={`tavla-item-${p.id}`} className="flex items-center gap-3 rounded-lg border border-white/8 bg-[#14161A] p-3">
                {p.pinned && <Pin className="w-4 h-4 text-amber-500 shrink-0" />}
                <div className="flex-1 min-w-0">
                  <div className="text-white text-sm font-semibold truncate">{p.title || "(utan rubrik)"}</div>
                  <div className="text-white/40 text-xs truncate">{new Date(p.created_at).toLocaleString("sv-SE")} · {(p.attachments || []).length} bilaga(or)</div>
                </div>
                <button onClick={() => togglePin(p)} title="Fäst" className={`p-2 ${p.pinned ? "text-amber-500" : "text-white/40 hover:text-amber-500"}`}><Pin className="w-4 h-4" /></button>
                <button onClick={() => startEdit(p)} className="px-2 text-xs text-white/60 hover:text-amber-500" data-testid={`tavla-edit-${p.id}`}>Ändra</button>
                <button onClick={() => del(p.id)} className="p-2 text-white/40 hover:text-red-500" data-testid={`tavla-delete-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
